/**
 * 返回键拦截处理器
 * 实现类似 App 的返回键体验：弹窗 -> 页面 -> 退出确认
 */
class BackButtonHandler {
  constructor() {
    this.isInitialized = false;
    this.historyStack = [];
    this.currentState = null;
    this.callbacks = {
      onModalClose: null,
      onPageBack: null,
      onExitConfirm: null,
      onStateChange: null
    };
    
    // 防抖相关
    this.lastExitConfirmTime = 0;
    this.exitConfirmDebounceTime = 1000; // 1秒内不重复显示退出确认
    
    // 绑定方法
    this.handlePopState = this.handlePopState.bind(this);
    this.pushState = this.pushState.bind(this);
  }

  /**
   * 初始化返回键拦截
   * @param {Object} options 配置选项
   */
  init(options = {}) {
    if (this.isInitialized) {
      console.warn('BackButtonHandler 已经初始化过了');
      return;
    }

    this.callbacks = { ...this.callbacks, ...options };
    
    // 添加初始状态到历史栈
    this.pushState('init', '初始化');
    
    // 监听 popstate 事件
    window.addEventListener('popstate', this.handlePopState);
    
    this.isInitialized = true;
    console.log('✅ 返回键拦截已初始化');
  }

  /**
   * 销毁处理器
   */
  destroy() {
    if (!this.isInitialized) return;
    
    window.removeEventListener('popstate', this.handlePopState);
    this.historyStack = [];
    this.currentState = null;
    this.isInitialized = false;
    
    console.log('✅ 返回键拦截已销毁');
  }

  /**
   * 推入新状态到历史栈
   * @param {string} state 状态标识
   * @param {string} title 状态标题
   * @param {Object} data 附加数据
   */
  pushState(state, title = '', data = {}) {
    const stateData = {
      state,
      title,
      data,
      timestamp: Date.now()
    };
    
    this.historyStack.push(stateData);
    this.currentState = stateData;
    
    // 推入浏览器历史栈
    window.history.pushState(stateData, title, window.location.href);
    
    console.log('📝 推入状态:', state, '历史栈长度:', this.historyStack.length);
    
    // 触发状态变化回调
    if (this.callbacks.onStateChange) {
      this.callbacks.onStateChange(stateData, this.historyStack);
    }
  }

  /**
   * 处理返回键事件
   * @param {Event} event popstate 事件
   */
  handlePopState(event) {
    console.log('🔙 [返回键拦截] 返回键被触发', event.state);
    
    // 同步更新历史栈：浏览器历史栈已经减少，我们需要从自己的历史栈中移除最后一个状态
    if (this.historyStack.length > 0) {
      const removedState = this.historyStack.pop();
      console.log('🔙 [返回键拦截] 同步移除历史栈状态:', removedState.state, '剩余长度:', this.historyStack.length);
      
      // 更新当前状态
      if (this.historyStack.length > 0) {
        this.currentState = this.historyStack[this.historyStack.length - 1];
      } else {
        this.currentState = null;
      }
    }
    
    // 检查是否有弹窗需要关闭
    if (this.hasOpenModals()) {
      console.log('🔙 [返回键拦截] 检测到弹窗，执行关闭');
      this.closeModals();
      return;
    }
    
    // 优先检查历史栈，如果有历史栈则执行页面返回
    if (this.historyStack.length > 0) {
      console.log('🔙 [返回键拦截] 历史栈不为空，执行页面返回');
      this.goBackToPrevPage();
      return;
    }
    
    // 历史栈为空时，显示退出确认
    console.log('🔙 [返回键拦截] 历史栈为空，显示退出确认');
    this.showExitConfirm();
  }

  /**
   * 检查是否有打开的弹窗
   * @returns {boolean}
   */
  hasOpenModals() {
    // 检查各种弹窗状态 - 使用实际存在的选择器
    const modalSelectors = [
      '.upload-dialog-overlay',
      '.tag-drawer-overlay', 
      '.delete-confirm-modal',
      '.rename-modal-overlay',
      '.alert-modal',
      '.search-overlay',
      '.bottom-menu-overlay',
      '.exit-confirm-modal',
      '.crop-modal-overlay',
      '.crop-preview-modal-overlay',
      '.tag-management-overlay',
      '.video-fullscreen-modal'  // 🎬 新增：检查视频全屏模态框
    ];
    
    // 检查弹窗是否存在且可见
    const hasVisibleModal = modalSelectors.some(selector => {
      const element = document.querySelector(selector);
      if (!element) return false;
      
      const style = getComputedStyle(element);
      const isVisible = style.display !== 'none' && 
                       style.visibility !== 'hidden' && 
                       style.opacity !== '0';
      
      return isVisible && !element.classList.contains('closing');
    });
    
    // 🎵 检查播放页面是否打开
    const lyricPlayer = document.querySelector('.lyric-player');
    const isLyricPlayerOpen = lyricPlayer && lyricPlayer.classList.contains('show');
    
    console.log('🔍 [弹窗检测] 检查弹窗状态:', {
      hasVisibleModal,
      isLyricPlayerOpen,
      lyricPlayerExists: !!lyricPlayer,
      lyricPlayerClasses: lyricPlayer ? lyricPlayer.className : 'N/A'
    });
    
    return hasVisibleModal || isLyricPlayerOpen;
  }

  /**
   * 关闭所有弹窗
   */
  closeModals() {
    console.log('🚪 [返回键拦截] 关闭弹窗');
    
    // 触发弹窗关闭回调
    if (this.callbacks.onModalClose) {
      console.log('🚪 [返回键拦截] 执行弹窗关闭回调');
      this.callbacks.onModalClose();
    } else {
      console.warn('⚠️ [返回键拦截] 没有设置弹窗关闭回调');
    }
    
    // 推入新状态，防止直接退出
    this.pushState('modal-closed', '弹窗已关闭');
  }

  /**
   * 检查是否在子页面
   * @returns {boolean}
   */
  isInSubPage() {
    // 检查当前页面状态
    const currentPath = window.location.pathname;
    const musicPages = ['/music', '/test-music-list'];
    
    // 如果在音乐页面但不是首页
    if (musicPages.includes(currentPath)) {
      // 检查是否有页面切换状态
      const musicContainer = document.querySelector('.music-list-container');
      if (musicContainer) {
        const activeSection = musicContainer.querySelector('.page-item.active');
        if (activeSection) {
          const pageName = activeSection.dataset.page;
          const isInSubPage = pageName !== '音乐';
          console.log('🔍 [页面检测] 当前页面:', pageName, '是否在子页面:', isInSubPage);
          return isInSubPage; // 如果不在音乐页面，认为在子页面
        }
      }
    }
    
    console.log('🔍 [页面检测] 不在音乐页面，返回false');
    return false;
  }

  /**
   * 返回上一页面
   */
  goBackToPrevPage() {
    console.log('⬅️ 返回上一页面');
    
    // 触发页面返回回调
    if (this.callbacks.onPageBack) {
      this.callbacks.onPageBack();
    }
    
    // 注意：返回键不应该推入新状态，因为返回键本身就是popstate事件触发的
    // 页面返回逻辑应该在onPageBack回调中处理历史栈的弹出
  }

  /**
   * 显示退出确认
   */
  showExitConfirm() {
    const now = Date.now();
    
    // 防抖：如果距离上次显示退出确认时间太短，则忽略
    if (now - this.lastExitConfirmTime < this.exitConfirmDebounceTime) {
      console.log('❓ [返回键拦截] 防抖：忽略重复的退出确认请求');
      return;
    }
    
    this.lastExitConfirmTime = now;
    console.log('❓ [返回键拦截] 显示退出确认');
    
    // 触发退出确认回调
    if (this.callbacks.onExitConfirm) {
      this.callbacks.onExitConfirm();
    } else {
      // 默认确认对话框
      if (confirm('确认退出本网站吗？')) {
        // 用户确认退出，清空历史栈并帮助用户返回上一页
        console.log('❓ [返回键拦截] 用户确认退出，帮助用户返回上一页');
        this.clearHistory();
        // 帮助用户返回上一页，如果无法返回则跳转到首页
        if (window.history.length > 1) {
          window.history.back();
        } else {
          // 如果没有历史记录，跳转到首页
          window.location.href = '/';
        }
      } else {
        // 用户取消，推入一个状态确保下次按返回键仍会触发退出确认
        console.log('❓ [返回键拦截] 用户取消退出，推入状态确保下次仍会显示退出确认');
        this.pushState('exit-cancelled', '用户取消退出');
      }
    }
  }

  /**
   * 获取当前状态
   * @returns {Object}
   */
  getCurrentState() {
    return this.currentState;
  }

  /**
   * 获取历史栈
   * @returns {Array}
   */
  getHistoryStack() {
    return [...this.historyStack];
  }

  /**
   * 清空历史栈（谨慎使用）
   */
  clearHistory() {
    this.historyStack = [];
    this.currentState = null;
    console.log('🗑️ 历史栈已清空');
  }
}

// 创建单例实例
const backButtonHandler = new BackButtonHandler();

export default backButtonHandler;
